import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const AnalyticsOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('traffic');
  const [analyticsData, setAnalyticsData] = useState({
    traffic: null,
    acquisition: null,
    content: null,
    conversion: null,
    technical: null
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Fetch overview data
      const response = await fetch(`${backendUrl}/api/admin/analytics/overview?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Store the full response data
        setAnalyticsData({
          traffic: data.website || data,
          acquisition: data.website || data,
          content: data.website || data,
          conversion: data.leads || data,
          technical: data.website || data,
          leads: data.leads || null,
          website: data.website || null
        });
      } else {
        console.error('Failed to fetch analytics:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 'traffic', label: 'Traffic Overview', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
    { id: 'acquisition', label: 'Acquisition', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    )},
    { id: 'content', label: 'Content Performance', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { id: 'conversion', label: 'Conversion Funnel', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
    { id: 'technical', label: 'Technical Snapshot', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )}
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading analytics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-[1400px]">
        {/* Page Header */}
        <div className="pb-5 border-b border-[#E5E7EB]">
          <h1 className="text-2xl font-bold text-[#111827] mb-1">Analytics</h1>
          <p className="text-[#6B7280] text-sm">Comprehensive analytics and insights</p>
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-1.5 inline-flex gap-1 flex-wrap">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? 'bg-[#1E40AF] text-white'
                  : 'text-[#6B7280] hover:bg-[#F3F4F6]'
              }`}
            >
              {section.icon}
              <span>{section.label}</span>
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
          {activeSection === 'traffic' && (
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Traffic Overview</h2>
              {analyticsData.website && analyticsData.website.available ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-[#F9FAFB] rounded-lg p-4">
                    <p className="text-sm text-[#6B7280] mb-1">Visitors Today</p>
                    <p className="text-2xl font-bold text-[#111827]">{analyticsData.website.visitors_today || 0}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-4">
                    <p className="text-sm text-[#6B7280] mb-1">Sessions Today</p>
                    <p className="text-2xl font-bold text-[#111827]">{analyticsData.website.sessions_today || 0}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-4">
                    <p className="text-sm text-[#6B7280] mb-1">Avg Session Duration</p>
                    <p className="text-2xl font-bold text-[#111827]">{analyticsData.website.avg_session_duration || 0}s</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-4">
                    <p className="text-sm text-[#6B7280] mb-1">Bounce Rate</p>
                    <p className="text-2xl font-bold text-[#111827]">{analyticsData.website.bounce_rate || 0}%</p>
                  </div>
                  {analyticsData.website.top_traffic_source && analyticsData.website.top_traffic_source !== 'N/A' && (
                    <div className="bg-[#F9FAFB] rounded-lg p-4 md:col-span-2">
                      <p className="text-sm text-[#6B7280] mb-1">Top Traffic Source</p>
                      <p className="text-lg font-semibold text-[#111827]">{analyticsData.website.top_traffic_source}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-[#6B7280]">
                  <p>Traffic analytics will be displayed here</p>
                  <p className="text-sm mt-2">GA4 integration pending - requires google-analytics-data library</p>
                  <p className="text-xs mt-1">Make sure Google Analytics 4 is configured in Settings → Analytics</p>
                </div>
              )}
            </div>
          )}

          {activeSection === 'acquisition' && (
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Acquisition</h2>
              <div className="text-center py-12 text-[#6B7280]">
                <p>Acquisition analytics will be displayed here</p>
                <p className="text-sm mt-2">Traffic channels, landing pages, and referrals</p>
              </div>
            </div>
          )}

          {activeSection === 'content' && (
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Content Performance</h2>
              <div className="text-center py-12 text-[#6B7280]">
                <p>Content performance analytics will be displayed here</p>
                <p className="text-sm mt-2">Top pages, blogs, universities, and programs</p>
              </div>
            </div>
          )}

          {activeSection === 'conversion' && (
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Conversion Funnel</h2>
              {analyticsData.leads ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-[#F9FAFB] rounded-lg p-4">
                      <p className="text-sm text-[#6B7280] mb-1">Leads Today</p>
                      <p className="text-2xl font-bold text-[#111827]">{analyticsData.leads.today?.count || 0}</p>
                      {analyticsData.leads.today?.change !== undefined && (
                        <p className={`text-sm mt-1 ${analyticsData.leads.today.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {analyticsData.leads.today.change >= 0 ? '+' : ''}{analyticsData.leads.today.change.toFixed(1)}% vs yesterday
                        </p>
                      )}
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-4">
                      <p className="text-sm text-[#6B7280] mb-1">Leads This Week</p>
                      <p className="text-2xl font-bold text-[#111827]">{analyticsData.leads.this_week?.count || 0}</p>
                      {analyticsData.leads.this_week?.change !== undefined && (
                        <p className={`text-sm mt-1 ${analyticsData.leads.this_week.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {analyticsData.leads.this_week.change >= 0 ? '+' : ''}{analyticsData.leads.this_week.change.toFixed(1)}% vs last week
                        </p>
                      )}
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-4">
                      <p className="text-sm text-[#6B7280] mb-1">Leads This Month</p>
                      <p className="text-2xl font-bold text-[#111827]">{analyticsData.leads.this_month?.count || 0}</p>
                      {analyticsData.leads.this_month?.change !== undefined && (
                        <p className={`text-sm mt-1 ${analyticsData.leads.this_month.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {analyticsData.leads.this_month.change >= 0 ? '+' : ''}{analyticsData.leads.this_month.change.toFixed(1)}% vs last month
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {analyticsData.leads.breakdown_by_type && (
                    <div>
                      <h3 className="text-base font-semibold text-[#111827] mb-3">Breakdown by Type</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {['today', 'this_week', 'this_month'].map((period) => (
                          <div key={period} className="bg-[#F9FAFB] rounded-lg p-4">
                            <p className="text-sm font-medium text-[#6B7280] mb-2 capitalize">{period.replace('_', ' ')}</p>
                            {Object.entries(analyticsData.leads.breakdown_by_type[period] || {}).map(([type, count]) => (
                              <div key={type} className="flex justify-between items-center py-1">
                                <span className="text-sm text-[#111827]">{type}</span>
                                <span className="text-sm font-semibold text-[#1E40AF]">{count}</span>
                              </div>
                            ))}
                            {Object.keys(analyticsData.leads.breakdown_by_type[period] || {}).length === 0 && (
                              <p className="text-sm text-[#6B7280]">No data</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {analyticsData.leads.top_universities && Object.keys(analyticsData.leads.top_universities).length > 0 && (
                    <div>
                      <h3 className="text-base font-semibold text-[#111827] mb-3">Top Universities by Leads</h3>
                      <div className="bg-[#F9FAFB] rounded-lg p-4">
                        {Object.entries(analyticsData.leads.top_universities).slice(0, 10).map(([uni, count]) => (
                          <div key={uni} className="flex justify-between items-center py-2 border-b border-[#E5E7EB] last:border-0">
                            <span className="text-sm text-[#111827]">{uni}</span>
                            <span className="text-sm font-semibold text-[#1E40AF]">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {analyticsData.leads.top_programs && Object.keys(analyticsData.leads.top_programs).length > 0 && (
                    <div>
                      <h3 className="text-base font-semibold text-[#111827] mb-3">Top Programs by Leads</h3>
                      <div className="bg-[#F9FAFB] rounded-lg p-4">
                        {Object.entries(analyticsData.leads.top_programs).slice(0, 10).map(([prog, count]) => (
                          <div key={prog} className="flex justify-between items-center py-2 border-b border-[#E5E7EB] last:border-0">
                            <span className="text-sm text-[#111827]">{prog}</span>
                            <span className="text-sm font-semibold text-[#1E40AF]">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-[#6B7280]">
                  <p>Conversion funnel analytics will be displayed here</p>
                  <p className="text-sm mt-2">Page views → enquiry → application conversion rates</p>
                </div>
              )}
            </div>
          )}

          {activeSection === 'technical' && (
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Technical Snapshot</h2>
              <div className="text-center py-12 text-[#6B7280]">
                <p>Technical metrics will be displayed here</p>
                <p className="text-sm mt-2">PSI scores and error rates (future-ready)</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
